/**
 * MetricTile.tsx — renders one labelled metric (label + value + optional unit).
 *
 * Boolean values render as a local‑agnostic check/dash glyph with an aria-label;
 * null renders as an em-dash "no data". Numbers are shown as-is (the BFF already
 * rounds), with the unit appended. Pure + presentational.
 */

import type { Metric } from '../api/types';

const formatValue = (value: Metric['value']): string => {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return String(value);
};

export interface MetricTileProps {
  metric: Metric;
}

export const MetricTile = ({ metric }: MetricTileProps) => {
  const display = formatValue(metric.value);
  return (
    <div
      data-testid="metric-tile"
      className="flex min-w-0 flex-col gap-0.5 rounded-md bg-surface-2 px-3 py-2"
    >
      <span className="text-meta text-ink-3">{metric.label}</span>
      <span className="break-all font-mono text-body tabular-nums text-ink-1">
        {display}
        {metric.unit ? <span className="ml-1 text-ink-3">{metric.unit}</span> : null}
      </span>
    </div>
  );
};
